import { AfterViewInit, Directive, ElementRef, HostListener, Input, OnDestroy } from "@angular/core";
import { AbstractControl } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, filter, takeUntil } from "rxjs/operators";
import { ToasterService } from "../../../core/services/toaster/toaster.service";
import { HttpErrorStatusCode, HttpSuccessStatusCode } from "../../../shared/enums/http-request-status-code.enum";


@Directive({
    selector: "[appNumberFormat]",
    standalone: false
})
export class NumberFormatDirective implements AfterViewInit, OnDestroy {
    @Input() amountController: AbstractControl;
    @Input() precision = 2;
    @Input() allowNegative = false;

    private _destroySubscriptions = new Subject();

    readonly regexComma = /,/g;
    readonly regexDot = /\./g;
    readonly regexLeadingComma = /^,/;
    readonly regexLeadingZeros = /^0+(?=\d)/;
    readonly regexMinus = /-/g;
    readonly regexNumber = /^\d/;
    readonly regexNumberCharacters = /^[-+]?(?:\d+[.,]?)*\d*$/;
    readonly regexPreserveSingleLeadingMinus = /^(-?)0+(\d)/;
    readonly regexSingleLeadingMinus = /(?!^)-/g;

    constructor(
        private el: ElementRef<HTMLInputElement>,
        private toaster: ToasterService,
        private translate: TranslateService
    ) { }

    ngOnDestroy(): void {
        this._destroySubscriptions.next(undefined);
        this._destroySubscriptions.complete();
    }

    ngAfterViewInit(): void {
        this.amountController.valueChanges
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                filter(value => !!value),
                takeUntil(this._destroySubscriptions)
            )
            .subscribe((value: string) => {
                let formattedValue = value.toString().replace(".", ",");

                if (this.regexLeadingComma.test(formattedValue)) {
                    formattedValue = `0${formattedValue}`;
                }

                if (formattedValue.includes(",")) {
                    const [intPart, decimalPart = ""] = formattedValue.split(",");
                    formattedValue = intPart + "," + decimalPart.substring(0, this.precision);
                }

                formattedValue = formattedValue.replace(this.regexLeadingZeros, "");

                if (formattedValue !== value) {
                    this.amountController.setValue(formattedValue, { emitEvent: false });
                }
            });
    }

    @HostListener("keydown", ["$event"])
    onKeyDown(event: KeyboardEvent): void {
        const input = this.el.nativeElement;
        const value = input.value;
        const key = event.key;
        const isCtrlOrMeta = event.ctrlKey || event.metaKey;

        if (this.shouldBlockNonNumericKey(key, isCtrlOrMeta)) {
            event.preventDefault();
            return;
        }

        if (this.shouldBlockMinus(key, value, input.selectionStart)) {
            event.preventDefault();
            return;
        }

        if (this.shouldBlockDecimalSeparator(key)) {
            event.preventDefault();
            return;
        }

        const separator = this.getSeparator(value, key);

        if (separator) {
            if (this.shouldBlockDecimalPrecision(input, value, separator, key)) {
                event.preventDefault();
                return;
            }
            if (this.shouldBlockSecondSeparator(key, value)) {
                event.preventDefault();
                return;
            }
        }
    }

    private shouldBlockNonNumericKey(key: string, isCtrlOrMeta: boolean): boolean {
        return !isCtrlOrMeta && !this.regexNumber.test(key) && key !== "," && key !== "." && key.length === 1;
    }

    private shouldBlockMinus(key: string, value: string, selectionStart: number | null): boolean {
        return key === "-" && (!this.allowNegative || value.includes("-") || selectionStart !== 0);
    }

    private shouldBlockDecimalSeparator(key: string): boolean {
        return this.precision === 0 && (key === "," || key === ".");
    }

    private getSeparator(value: string, key: string): string | null {
        if (value.includes(",")) return ",";
        if (value.includes(".")) return ".";
        if (key === "," || key === ".") return key;
        return null;
    }

    private shouldBlockDecimalPrecision(
        input: HTMLInputElement,
        value: string,
        separator: string,
        key: string
    ): boolean {
        const [_, decimal = ""] = value.split(separator);
        const cursorAfterSeparator = value.indexOf(separator) >= 0 && (input.selectionStart ?? 0) > value.indexOf(separator);
        return cursorAfterSeparator && decimal.length >= this.precision && this.regexNumber.test(key);
    }

    private shouldBlockSecondSeparator(key: string, value: string): boolean {
        return (key === "," || key === ".") && (value.includes(",") || value.includes("."));
    }


    @HostListener("paste", ["$event"])
    onPaste(event: ClipboardEvent) {
        event.preventDefault();

        const pastedText = event.clipboardData?.getData("text") ?? "";
        const text = pastedText.trim(); // Remove leading and trailing spaces only

        // Prevent paste if text contains anything other than +, -, 0-9, dot, comma. "-" or "+" must be leading.
        if (!this.regexNumberCharacters.test(text)) {
            this.toaster.errorMethod({
                message: this.translate.instant("MESSAGES.NUMBER_FORMAT_PASTE_PREVENTED"),
                statusCode: HttpErrorStatusCode.BAD_REQUEST
            });
            return;
        }

        const dotCount = (text.match(this.regexDot) || []).length;
        const commaCount = (text.match(this.regexComma) || []).length;

        let formatted = text;
        let showToaster = false;
        if (dotCount > 1) {
            // Dots are thousands separators, remove all dots
            formatted = formatted.replace(this.regexDot, "");
        } else if (commaCount > 1) {
            // Commas are thousands separators, remove all commas
            formatted = formatted.replace(this.regexComma, "");
            // If there's a dot, treat as decimal and swap to comma
            formatted = formatted.replace(".", ",");
            showToaster = true;
        } else if (dotCount === 1 && commaCount === 1) {
            // Both present, assume last one is decimal
            if (text.lastIndexOf(".") > text.lastIndexOf(",")) {
                // Dot is decimal, remove all commas (thousands)
                formatted = formatted.replace(this.regexComma, "");
                formatted = formatted.replace(".", ",");
                showToaster = true;
            } else {
                // Comma is decimal, remove all dots (thousands)
                formatted = formatted.replace(this.regexDot, "");
            }
        } else if (dotCount === 1 && commaCount === 0) {
            // Single dot, assume decimal, swap to comma
            formatted = formatted.replace(".", ",");
            showToaster = true;
        }

        if (showToaster) {
            this.toaster.successMethod({
                message: this.translate.instant("MESSAGES.NUMBER_FORMAT_AUTO_FORMATTED"),
                statusCode: HttpSuccessStatusCode.OK
            });
        }

        const dutchFormatted = this.formatDutchNumber(formatted);
        this.amountController.setValue(dutchFormatted as any, { emitEvent: true });
        this.el.nativeElement.value = dutchFormatted;

        // Mark control as touched and dirty
        this.amountController.markAsTouched();
        this.amountController.markAsDirty();
    }

    formatDutchNumber(input: string): string {
        let formatted = input;

        // Only allow one leading minus
        if (this.allowNegative) {
            formatted = formatted.replace(this.regexSingleLeadingMinus, "");
        } else {
            formatted = formatted.replace(this.regexMinus, "");
        }

        // Remove leading zeros (preserve minus)
        formatted = formatted.replace(this.regexPreserveSingleLeadingMinus, "$1$2");

        // If the result is just '-' or empty, return '0'
        if (formatted === "-0" || formatted === "-" || formatted === "") {
            return "0";
        }

        // Ensure only two decimal places after the comma
        if (formatted.includes(",")) {
            const [intPart, decimalPart = ""] = formatted.split(",");
            formatted = intPart + "," + decimalPart.substring(0, 2);
        }

        return formatted;
    }
}
